import { SHAPES, TYPES, FILL_TYPES } from '../../../render/constants';
import { renderNodes } from '../../../lib/gen/render/render';
import { wrapText } from '../../../render/text';
import { deserializePoints } from '../../../render/line';
import { resolveStrokeWidth, resolveStrokeColor } from '../../../render/utils/stroke';
import { resolveFillType } from '../../../render/utils/background';

async function setupPPTXGen() {
  const mod = await import('pptxgenjs');
  return mod.default || mod;
}

async function initJSZip() {
  const mod = await import('jszip');
  return mod.default || mod;
}

/**
 * Fix pptxgenjs bug: removes invalid slideMaster references from [Content_Types].xml
 * pptxgenjs incorrectly declares multiple slideMasters in Content_Types but only generates slideMaster1.xml
 *
 * @param {Blob} pptxBlob - The generated PPTX blob
 * @returns {Promise<Blob>} - The fixed PPTX blob
 */
async function fixSlideMasterReferences(pptxBlob) {
  const JSZip = await initJSZip();
  const zip = await JSZip.loadAsync(pptxBlob);

  // Read [Content_Types].xml
  const contentTypesFile = zip.file('[Content_Types].xml');
  if (!contentTypesFile) {
    return pptxBlob; // No Content_Types found, return original
  }

  let contentTypesXml = await contentTypesFile.async('string');

  // Check which slideMasters actually exist
  const existingSlideMasters = new Set();
  zip.folder('ppt/slideMasters')?.forEach((relativePath) => {
    const match = relativePath.match(/^slideMaster(\d+)\.xml$/);
    if (match) {
      existingSlideMasters.add(parseInt(match[1]));
    }
  });

  // Remove references to non-existent slideMasters from Content_Types
  // Match pattern: <Override PartName="/ppt/slideMasters/slideMasterN.xml" ... />
  contentTypesXml = contentTypesXml.replace(
    /<Override\s+PartName="\/ppt\/slideMasters\/slideMaster(\d+)\.xml"[^>]*\/>/g,
    (match, num) => {
      const masterNum = parseInt(num);
      if (existingSlideMasters.has(masterNum)) {
        return match; // Keep existing master references
      }
      return ''; // Remove non-existent master references
    }
  );

  // Update the zip with fixed Content_Types
  zip.file('[Content_Types].xml', contentTypesXml);

  // Generate fixed blob
  return await zip.generateAsync({ type: 'blob', mimeType: pptxBlob.type });
}

const PX_TO_INCH = 1 / 96;
const PX_TO_PT = 0.75;

/**
 * Round a number to a fixed precision to avoid floating-point issues in PPT.
 * PowerPoint uses EMU (914400 per inch), so we limit to 6 decimal places
 * which provides sub-EMU precision while avoiding floating-point noise.
 *
 * @param {number} value - The value to round
 * @param {number} precision - Number of decimal places (default: 6)
 * @returns {number} The rounded value
 */
function roundToPrecision(value, precision = 6) {
  if (typeof value !== 'number' || !isFinite(value)) return 0;
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
}

/**
 * Convert pixels to inches with proper precision for PPT compatibility.
 * Ensures the result is a valid positive number.
 *
 * @param {number} px - Value in pixels
 * @returns {number} Value in inches, rounded to 6 decimal places
 */
function pxToInch(px) {
  if (typeof px !== 'number' || !isFinite(px)) return 0;
  const result = px * PX_TO_INCH;
  return roundToPrecision(result);
}

/**
 * Convert pixels to points with proper precision for PPT compatibility.
 *
 * @param {number} px - Value in pixels
 * @returns {number} Value in points, rounded to 4 decimal places
 */
function pxToPt(px) {
  if (typeof px !== 'number' || !isFinite(px)) return 0;
  const result = px * PX_TO_PT;
  return roundToPrecision(result, 4);
}

/**
 * Ensure a dimension (width/height) is valid for PPT.
 * PPT requires positive dimensions; zero or negative values cause corruption.
 *
 * @param {number} value - The dimension value in inches
 * @param {number} minValue - Minimum valid value (default: 0.001 inch ≈ 0.1px)
 * @returns {number} A valid positive dimension
 */
function ensureValidDimension(value, minValue = 0.001) {
  if (typeof value !== 'number' || !isFinite(value) || value < minValue) {
    return minValue;
  }
  return value;
}

/**
 * Check if a color string contains a gradient definition.
 */
function isGradient(colorStr) {
  return colorStr && typeof colorStr === 'string' && colorStr.includes('gradient');
}

/**
 * Check if a fill color is transparent (no fill, 'none', 'transparent', or fully transparent rgba).
 */
function isTransparentFill(fill) {
  if (!fill || fill === 'none' || fill === 'transparent') return true;

  // Check for rgba with alpha = 0
  const rgbaMatch = fill.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch && rgbaMatch[4] !== undefined) {
    const alpha = parseFloat(rgbaMatch[4]);
    if (alpha === 0) return true;
  }

  return false;
}

/**
 * Normalize `node.radius` into a numeric pixel value.
 * Supports:
 * - number
 * - "10", "10px"
 * - "10px 5px 0 0" -> use the first value as an approximate overall radius
 * Percentage and complex CSS forms are not mapped 1:1 in PPT, so we treat them as 0.
 */
function getNumericRadius(node) {
  const radius = node && node.radius;
  if (!radius) return 0;

  if (typeof radius === 'number') {
    return radius > 0 ? radius : 0;
  }

  if (typeof radius === 'string') {
    // Take the first token, strip "px" and parse
    const first = radius.trim().split(/\s+/)[0] || '';
    const cleaned = first.replace(/px$/i, '');
    const value = parseFloat(cleaned);
    if (!isNaN(value) && value > 0) {
      return value;
    }
  }

  return 0;
}

/**
 * Parse radius into four corner values [topLeft, topRight, bottomRight, bottomLeft].
 * Supports CSS border-radius syntax:
 * - Single value: "10" or 10 -> all corners same
 * - Two values: "10 5" -> [10, 5, 10, 5] (vertical, horizontal)
 * - Three values: "10 5 3" -> [10, 5, 3, 5]
 * - Four values: "10 5 3 8" -> [10, 5, 3, 8]
 */
function parseRadiusCorners(radius) {
  if (!radius) return [0, 0, 0, 0];

  if (typeof radius === 'number') {
    const v = radius > 0 ? radius : 0;
    return [v, v, v, v];
  }

  if (typeof radius === 'string') {
    const parts = radius
      .trim()
      .split(/\s+/)
      .map((p) => {
        const cleaned = p.replace(/px$/i, '');
        const val = parseFloat(cleaned);
        return !isNaN(val) && val > 0 ? val : 0;
      });

    if (parts.length === 1) {
      return [parts[0], parts[0], parts[0], parts[0]];
    } else if (parts.length === 2) {
      return [parts[0], parts[1], parts[0], parts[1]];
    } else if (parts.length === 3) {
      return [parts[0], parts[1], parts[2], parts[1]];
    } else {
      return [parts[0], parts[1], parts[2], parts[3]];
    }
  }

  return [0, 0, 0, 0];
}

/**
 * Build a radius string from four corner values.
 * Optimizes to shortest form when possible.
 */
function buildRadiusString(corners) {
  const [tl, tr, br, bl] = corners;

  // All same -> single value
  if (tl === tr && tr === br && br === bl) {
    return tl > 0 ? tl : 0;
  }

  // Return four values
  return `${tl} ${tr} ${br} ${bl}`;
}

export async function node2PPT(store, nodes, { onProgress } = {}) {
  const PptxGenJS = await setupPPTXGen();
  const pres = new PptxGenJS();

  if (!Array.isArray(nodes)) {
    nodes = [nodes];
  }

  const frames = nodes.filter((n) => n.type === TYPES.frame);
  const otherNodes = nodes.filter((n) => n.type !== TYPES.frame);

  let layoutW = 1280;
  let layoutH = 720;

  if (frames.length > 0) {
    layoutW = frames[0].w;
    layoutH = frames[0].h;
  } else if (otherNodes.length > 0) {
    const rect = store.api.getNodesRegion(otherNodes);
    layoutW = rect.w;
    layoutH = rect.h;
  }

  pres.defineLayout({
    name: 'CUSTOM',
    width: ensureValidDimension(pxToInch(layoutW)),
    height: ensureValidDimension(pxToInch(layoutH)),
  });
  pres.layout = 'CUSTOM';
  const frame = frames[0];
  // 设置 PPT 元数据
  pres.author = 'Made with Seede AI';
  pres.company = 'Seede AI';
  pres.title = frame?.name || 'Created by Seede AI';
  pres.subject = 'Presentation';

  // Collect all children for progress calculation
  const frameChildrenCounts = frames.map((frame) => {
    const children = store.api.getChildren(frame.id) || [];
    return children.length || 1; // At least 1 for the frame itself
  });
  const totalNodes = frameChildrenCounts.reduce((sum, c) => sum + c, 0) + otherNodes.length;
  let processedNodes = 0;
  let currentPage = 0;

  const renderListToSlide = async (nodeList, slide, originX, originY, pageIndex) => {
    for (const node of nodeList) {
      slide.name = node.name || `Slide ${pageIndex}`;
      await renderNode(node, slide, store, { originX, originY });
      processedNodes++;
      if (onProgress) {
        onProgress(processedNodes / totalNodes, pageIndex);
      }
    }
  };

  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    const slide = pres.addSlide();
    currentPage = i + 1;

    // 渲染 slide 背景（支持纯色、渐变、图片等）
    // 使用全屏元素作为背景，避免 pptxgenjs 为每个 slide 创建多个 slideMaster 的 bug
    await renderSlideBackground(store, slide, frame, layoutW, layoutH);

    const frameBox = store.api.getNodeBox(frame);
    const children = store.api.getChildren(frame.id) || [];

    if (children.length > 0) {
      await renderListToSlide(children, slide, frameBox.x, frameBox.y, currentPage);
    } else {
      // No children, still report progress for this frame
      processedNodes++;
      if (onProgress) {
        onProgress(processedNodes / totalNodes, currentPage);
      }
    }
  }

  if (otherNodes.length > 0) {
    const slide = pres.addSlide();
    currentPage = frames.length + 1;

    const rect = store.api.getNodesRegion(otherNodes);
    await renderListToSlide(otherNodes, slide, rect.x, rect.y, currentPage);
  }

  const pptxBlob = await pres.write({ outputType: 'blob' });

  // Fix pptxgenjs bug: remove invalid slideMaster references from Content_Types
  return await fixSlideMasterReferences(pptxBlob);
}

/**
 * Render slide background supporting solid color, gradient, image, and other fill types.
 * Uses a full-screen shape/image instead of slide.background to avoid pptxgenjs slideMaster bug.
 *
 * @param {object} store - The store context
 * @param {object} slide - The pptxgenjs slide object
 * @param {object} frame - The frame node with fill property
 * @param {number} layoutW - Layout width in pixels
 * @param {number} layoutH - Layout height in pixels
 */
async function renderSlideBackground(store, slide, frame, layoutW, layoutH) {
  const fill = frame.fill;
  const w = ensureValidDimension(pxToInch(layoutW));
  const h = ensureValidDimension(pxToInch(layoutH));

  // 如果是透明或无填充，使用白色作为默认背景
  if (isTransparentFill(fill)) {
    slide.addShape('rect', {
      x: 0,
      y: 0,
      w,
      h,
      fill: { color: 'FFFFFF' },
      line: { width: 0, color: 'FFFFFF', transparency: 100 },
    });
    return;
  }

  const fillType = resolveFillType(fill);

  // 渐变、图片、动态着色器、图案等复杂背景需要渲染为图片
  const needsImageRendering = [
    FILL_TYPES.GRADIENT,
    FILL_TYPES.IMAGE,
    FILL_TYPES.DYNAMIC,
    FILL_TYPES.PATTERN,
  ].includes(fillType);

  if (needsImageRendering) {
    try {
      // 创建一个临时节点来渲染背景，保留 frame 的所有属性
      const bgNode = {
        ...frame,
        type: TYPES.shape,
        shape: SHAPES.rect,
        // 重置位置到原点，因为我们只渲染背景
        x: 0,
        y: 0,
        w: layoutW,
        h: layoutH,
      };
      const dataUrl = await renderNodes(store, {
        nodes: [bgNode],
        contentType: 'image/png',
      });
      if (dataUrl) {
        slide.addImage({
          x: 0,
          y: 0,
          w,
          h,
          data: dataUrl,
        });
        return;
      }
    } catch (err) {
      console.warn('Failed to render complex slide background as image:', err);
      // Fall through to solid color fallback
    }
  }

  // 纯色背景或降级处理
  const bgColor = parseHex(fill) || 'FFFFFF';
  slide.addShape('rect', {
    x: 0,
    y: 0,
    w,
    h,
    fill: { color: bgColor },
    line: { width: 0, color: 'FFFFFF', transparency: 100 },
  });
}

async function renderFrame(node, slide, store, { x, y, w, h }, { originX, originY }) {
  // 如果填充是渐变，将 frame 背景渲染为图片
  if (isGradient(node.fill)) {
    try {
      // 创建一个只有背景的临时 shape 节点来渲染渐变
      const bgNode = {
        ...node,
        type: TYPES.shape,
        // 确保不渲染子元素，只渲染背景
      };
      const dataUrl = await renderNodes(store, {
        nodes: [bgNode],
        contentType: 'image/png',
      });
      if (dataUrl) {
        const imgOpts = { x, y, w, h, data: dataUrl };
        // Apply opacity to image
        if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
          imgOpts.transparency = Math.round((1 - node.opacity) * 100);
        }
        slide.addImage(imgOpts);
      }
    } catch (err) {
      console.warn('Failed to render gradient frame background as image:', err);
      // Fall through to regular handling below
    }
  } else if (node.fill) {
    // 非渐变填充，复用 rect 相关绘制能力
    const { shapeType, opts, borderLines } = buildRectShapeOpts(
      node,
      { x, y, w, h },
      { fillValue: node.fill }
    );

    if (opts.fill || opts.line) {
      slide.addShape(shapeType, opts);
    }

    // Draw border lines for non-uniform stroke widths
    for (const borderLine of borderLines) {
      slide.addShape('line', borderLine);
    }
  }

  const children = store.api.getChildren(node.id) || [];
  for (const child of children) {
    await renderNode(child, slide, store, { originX, originY });
  }
}

/**
 * Compute inherited radius for a child node based on its alignment with the group.
 * Returns the inherited radius value, or null if no inheritance should occur.
 *
 * @param {object} child - The child node
 * @param {object} childBox - The child's bounding box { x, y, w, h }
 * @param {object} groupNode - The parent group node
 * @param {object} groupBox - The group's bounding box { x, y, w, h }
 * @returns {string|number|null} The inherited radius, or null if not applicable
 */
function computeInheritedRadius(child, childBox, groupNode, groupBox) {
  const groupRadius = getNumericRadius(groupNode);

  // Only process shape/image children when group has radius
  if (groupRadius <= 0) return null;
  if (child.type !== TYPES.shape && child.type !== TYPES.image) return null;

  // Skip if child already has its own radius
  if (getNumericRadius(child)) return null;

  const TOLERANCE = 1; // 1px tolerance for edge detection

  // Detect edge alignment
  const alignsLeft = Math.abs(childBox.x - groupBox.x) < TOLERANCE;
  const alignsRight = Math.abs(childBox.x + childBox.w - (groupBox.x + groupBox.w)) < TOLERANCE;
  const alignsTop = Math.abs(childBox.y - groupBox.y) < TOLERANCE;
  const alignsBottom = Math.abs(childBox.y + childBox.h - (groupBox.y + groupBox.h)) < TOLERANCE;

  // Check size matching
  const sameWidth = Math.abs(childBox.w - groupBox.w) < TOLERANCE;
  const sameHeight = Math.abs(childBox.h - groupBox.h) < TOLERANCE;

  // Parse group's corner radii [topLeft, topRight, bottomRight, bottomLeft]
  const groupCorners = parseRadiusCorners(groupNode.radius);
  let inheritedCorners = [0, 0, 0, 0];
  let shouldInherit = false;

  // Full match: same size and position -> inherit all corners
  if (sameWidth && sameHeight && alignsLeft && alignsTop) {
    inheritedCorners = groupCorners;
    shouldInherit = true;
  }
  // Left edge aligned + full height -> inherit left corners (topLeft, bottomLeft)
  else if (alignsLeft && sameHeight && alignsTop && alignsBottom) {
    inheritedCorners[0] = groupCorners[0]; // topLeft
    inheritedCorners[3] = groupCorners[3]; // bottomLeft
    shouldInherit = groupCorners[0] > 0 || groupCorners[3] > 0;
  }
  // Right edge aligned + full height -> inherit right corners (topRight, bottomRight)
  else if (alignsRight && sameHeight && alignsTop && alignsBottom) {
    inheritedCorners[1] = groupCorners[1]; // topRight
    inheritedCorners[2] = groupCorners[2]; // bottomRight
    shouldInherit = groupCorners[1] > 0 || groupCorners[2] > 0;
  }
  // Top edge aligned + full width -> inherit top corners (topLeft, topRight)
  else if (alignsTop && sameWidth && alignsLeft && alignsRight) {
    inheritedCorners[0] = groupCorners[0]; // topLeft
    inheritedCorners[1] = groupCorners[1]; // topRight
    shouldInherit = groupCorners[0] > 0 || groupCorners[1] > 0;
  }
  // Bottom edge aligned + full width -> inherit bottom corners (bottomRight, bottomLeft)
  else if (alignsBottom && sameWidth && alignsLeft && alignsRight) {
    inheritedCorners[2] = groupCorners[2]; // bottomRight
    inheritedCorners[3] = groupCorners[3]; // bottomLeft
    shouldInherit = groupCorners[2] > 0 || groupCorners[3] > 0;
  }
  // Top-left corner aligned -> inherit topLeft corner
  else if (alignsTop && alignsLeft) {
    inheritedCorners[0] = groupCorners[0];
    shouldInherit = groupCorners[0] > 0;
  }
  // Top-right corner aligned -> inherit topRight corner
  else if (alignsTop && alignsRight) {
    inheritedCorners[1] = groupCorners[1];
    shouldInherit = groupCorners[1] > 0;
  }
  // Bottom-right corner aligned -> inherit bottomRight corner
  else if (alignsBottom && alignsRight) {
    inheritedCorners[2] = groupCorners[2];
    shouldInherit = groupCorners[2] > 0;
  }
  // Bottom-left corner aligned -> inherit bottomLeft corner
  else if (alignsBottom && alignsLeft) {
    inheritedCorners[3] = groupCorners[3];
    shouldInherit = groupCorners[3] > 0;
  }

  if (shouldInherit) {
    return buildRadiusString(inheritedCorners);
  }

  return null;
}

/**
 * Render a group node with smart radius inheritance for edge-aligned children.
 * When a child aligns with group edges and group has radius,
 * the corresponding corner radii are inherited for visual consistency.
 */
async function renderGroup(node, slide, store, { originX, originY }) {
  const children = store.api.getChildren(node.id) || [];
  const groupBox = store.api.getNodeBox(node);

  for (const child of children) {
    const childBox = store.api.getNodeBox(child);
    const inheritedRadius = computeInheritedRadius(child, childBox, node, groupBox);

    // Apply inherited radius if computed
    const childToRender = inheritedRadius != null ? { ...child, radius: inheritedRadius } : child;

    await renderNode(childToRender, slide, store, { originX, originY });
  }
}

async function renderNode(node, slide, store, { originX, originY }) {
  const box = store.api.getNodeBox(node);
  const x = pxToInch(box.x - originX);
  const y = pxToInch(box.y - originY);
  const w = ensureValidDimension(pxToInch(box.w));
  const h = ensureValidDimension(pxToInch(box.h));

  const commonOpts = { x, y, w, h };

  switch (node.type) {
    case TYPES.text:
      renderText(node, slide, commonOpts);
      break;
    case TYPES.image:
      await renderImage(store, node, slide, commonOpts);
      break;
    case TYPES.shape:
      await renderShape(node, slide, store, commonOpts);
      break;
    case TYPES.line:
      renderLine(node, slide, commonOpts);
      break;
    case TYPES.path:
      await renderPath(node, slide, store, commonOpts);
      break;
    case TYPES.group:
      await renderGroup(node, slide, store, { originX, originY });
      break;
    case TYPES.frame:
      await renderFrame(node, slide, store, commonOpts, { originX, originY });
      break;
    default:
      break;
  }
}

/**
 * Parse a color string and return a valid 6-digit hex RGB value for PPT.
 * Supports:
 * - 6-digit hex: "#FFFFFF" or "FFFFFF" -> "FFFFFF"
 * - 3-digit hex: "#FFF" or "FFF" -> "FFFFFF" (expanded)
 * - 8-digit hex with alpha: "#FFFFFFAA" -> "FFFFFF" (alpha ignored)
 * - 4-digit hex with alpha: "#FFFA" -> "FFFFFF" (alpha ignored, expanded)
 * - rgb/rgba: "rgb(255, 255, 255)" or "rgba(255, 255, 255, 0.5)" -> "FFFFFF"
 * - Named colors are not supported, returns "000000"
 *
 * @param {string} colorStr - The color string to parse
 * @returns {string} A valid 6-digit hex RGB value (without #)
 */
function parseHex(colorStr) {
  if (
    !colorStr ||
    colorStr === 'none' ||
    colorStr === 'transparent' ||
    typeof colorStr !== 'string'
  ) {
    return '000000';
  }

  // Handle hex colors
  if (colorStr.startsWith('#') || /^[0-9a-fA-F]+$/.test(colorStr)) {
    let hex = colorStr.startsWith('#') ? colorStr.substring(1) : colorStr;

    // Remove alpha channel if present (8-digit or 4-digit hex)
    if (hex.length === 8) {
      hex = hex.substring(0, 6);
    } else if (hex.length === 4) {
      hex = hex.substring(0, 3);
    }

    // Expand 3-digit hex to 6-digit: "ABC" -> "AABBCC"
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    // Validate and return
    if (hex.length === 6 && /^[0-9a-fA-F]{6}$/.test(hex)) {
      return hex.toUpperCase();
    }
  }

  // Handle rgb/rgba colors
  const rgbaMatch = colorStr.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbaMatch) {
    const r = Math.min(255, Math.max(0, parseInt(rgbaMatch[1])))
      .toString(16)
      .padStart(2, '0');
    const g = Math.min(255, Math.max(0, parseInt(rgbaMatch[2])))
      .toString(16)
      .padStart(2, '0');
    const b = Math.min(255, Math.max(0, parseInt(rgbaMatch[3])))
      .toString(16)
      .padStart(2, '0');
    return `${r}${g}${b}`.toUpperCase();
  }

  // Fallback to black for unsupported formats
  return '000000';
}

function parseFill(colorStr) {
  if (!colorStr || colorStr === 'none') return null;

  if (colorStr.includes('gradient')) {
    // PPT 暂不支持复杂 CSS 渐变，这里退化为无填充，避免出现错误的纯色块
    // 返回 null 而不是 { type: 'none' }，因为 pptxgenjs 不支持 type: 'none' 格式
    return null;
  }

  const hex = parseHex(colorStr);
  let transparency = 0;

  const rgbaMatch = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch && rgbaMatch[4] !== undefined) {
    transparency = Math.round((1 - parseFloat(rgbaMatch[4])) * 100);
  }

  return { color: hex, transparency };
}

/**
 * Build shape options for rect-based rendering (fill, stroke, radius, opacity).
 * Used by renderShape, renderFrame, and text background rendering.
 *
 * @param {object} node - The node with stroke, opacity, radius properties
 * @param {object} box - The bounding box { x, y, w, h } in PPT coordinates (inches)
 * @param {object} options - Additional options
 * @param {string} options.fillValue - The fill color value to use
 * @param {boolean} options.includeDashProps - Whether to include strokeDasharray etc. (default: false)
 * @returns {object} { shapeType, opts, borderLines } for slide.addShape()
 *   - borderLines: Array of line definitions for non-uniform borders (PPT doesn't support per-side border widths)
 */
function buildRectShapeOpts(node, { x, y, w, h }, { fillValue, includeDashProps = false } = {}) {
  const opts = { x, y, w, h };
  const borderLines = []; // For non-uniform border widths

  // Handle fill with opacity
  if (fillValue) {
    const fill = parseFill(fillValue);
    if (fill && fill.type !== 'none') {
      opts.fill = fill;
      // Apply node opacity to fill
      if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
        const baseTransparency = fill.transparency || 0;
        const combinedTransparency = Math.min(
          100,
          Math.round(100 - (100 - baseTransparency) * node.opacity)
        );
        opts.fill.transparency = combinedTransparency;
      }
    }
  }
  // 如果没有有效填充，不设置 fill 属性（pptxgenjs 不支持 type: 'none' 格式）
  // pptxgenjs 会默认使用无填充

  // Handle stroke with proper opacity
  const strokeWidths = resolveStrokeWidth(node.strokeWidth);
  const strokeColors = resolveStrokeColor(node.stroke);

  // Check if stroke widths are uniform (all sides equal)
  const isUniformStroke =
    strokeWidths.top === strokeWidths.right &&
    strokeWidths.top === strokeWidths.bottom &&
    strokeWidths.top === strokeWidths.left;

  // 只有当 stroke 颜色非空且 strokeWidth > 0 时才渲染边框
  // 注意：即使有 strokeWidth，如果 stroke 为空字符串也不应该渲染边框
  if (!strokeColors.isEmpty && strokeColors.value && strokeWidths.value > 0) {
    let strokeTransparency = 0;

    // Extract alpha from rgba stroke color
    const rgbaMatch = strokeColors.value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (rgbaMatch && rgbaMatch[4] !== undefined) {
      strokeTransparency = Math.round((1 - parseFloat(rgbaMatch[4])) * 100);
    }

    // Apply strokeOpacity if specified
    if (
      typeof node.strokeOpacity === 'number' &&
      node.strokeOpacity >= 0 &&
      node.strokeOpacity < 1
    ) {
      strokeTransparency = Math.min(
        100,
        Math.round(100 - (100 - strokeTransparency) * node.strokeOpacity)
      );
    }

    // Apply node opacity to stroke
    if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
      strokeTransparency = Math.min(
        100,
        Math.round(100 - (100 - strokeTransparency) * node.opacity)
      );
    }

    if (isUniformStroke) {
      // Uniform stroke - use shape's native line property
      const strokeColor = parseHex(strokeColors.value);
      opts.line = {
        color: strokeColor,
        width: pxToPt(strokeWidths.value),
      };

      if (strokeTransparency > 0) {
        opts.line.transparency = strokeTransparency;
      }

      // Handle stroke dash properties (optional, for shape rendering)
      if (includeDashProps) {
        if (node.strokeDasharray) {
          const dashArray = node.strokeDasharray.split(/[\s,]+/).map(parseFloat);
          if (dashArray.length > 0 && dashArray[0] > 0) {
            opts.line.dashType = 'dash';
          }
        }
        if (node.strokeLinecap) {
          opts.line.endCap = node.strokeLinecap;
        }
        if (node.strokeLinejoin) {
          opts.line.lineJoin = node.strokeLinejoin;
        }
      }
    } else {
      // Non-uniform stroke - disable shape border and create separate lines for each side
      // 显式设置无边框：透明度 100% 的白色线条，宽度为 0
      opts.line = { width: 0, color: 'FFFFFF', transparency: 100 };

      // Build line options for each side that has a border
      const sides = [
        { name: 'top', width: strokeWidths.top, color: strokeColors.top },
        { name: 'right', width: strokeWidths.right, color: strokeColors.right },
        { name: 'bottom', width: strokeWidths.bottom, color: strokeColors.bottom },
        { name: 'left', width: strokeWidths.left, color: strokeColors.left },
      ];

      for (const side of sides) {
        if (side.width > 0) {
          const lineColor = parseHex(side.color || strokeColors.value);
          const lineOpts = {
            color: lineColor,
            width: pxToPt(side.width),
          };

          if (strokeTransparency > 0) {
            lineOpts.transparency = strokeTransparency;
          }

          // Handle stroke dash properties
          if (includeDashProps && node.strokeDasharray) {
            const dashArray = node.strokeDasharray.split(/[\s,]+/).map(parseFloat);
            if (dashArray.length > 0 && dashArray[0] > 0) {
              lineOpts.dashType = 'dash';
            }
          }

          // Calculate line coordinates based on side
          // Lines are drawn at the center of the border width
          const halfWidth = pxToInch(side.width) / 2;
          let lineX, lineY, lineW, lineH;

          switch (side.name) {
            case 'top':
              // Horizontal line at top edge
              lineX = x;
              lineY = y + halfWidth;
              lineW = w;
              lineH = 0;
              break;
            case 'bottom':
              // Horizontal line at bottom edge
              lineX = x;
              lineY = y + h - halfWidth;
              lineW = w;
              lineH = 0;
              break;
            case 'left':
              // Vertical line at left edge
              lineX = x + halfWidth;
              lineY = y;
              lineW = 0;
              lineH = h;
              break;
            case 'right':
              // Vertical line at right edge
              lineX = x + w - halfWidth;
              lineY = y;
              lineW = 0;
              lineH = h;
              break;
          }

          borderLines.push({
            x: lineX,
            y: lineY,
            w: lineW,
            h: lineH,
            line: lineOpts,
          });
        }
      }
    }
  }
  // 当没有有效 stroke 时，不设置 opts.line 属性
  // pptxgenjs 在不设置 line 时会使用默认样式，但对于 ellipse 等形状可能需要显式禁用
  // 如果仍然出现边框，可以尝试设置 line: { width: 0, color: 'FFFFFF', transparency: 100 }
  if (!opts.line) {
    // 显式设置无边框：透明度 100% 的白色线条，宽度为 0
    opts.line = { width: 0, color: 'FFFFFF', transparency: 100 };
  }

  // Handle radius - determine shape type (rect vs roundRect)
  const numericRadius = getNumericRadius(node);
  const minSide = Math.min(node.w || 0, node.h || 0);
  let shapeType = 'rect';

  if (numericRadius > 0 && minSide > 0) {
    shapeType = 'roundRect';
    const maxRadius = minSide / 2;
    const clamped = Math.max(0, Math.min(numericRadius, maxRadius));
    const ratio = clamped / maxRadius; // 0–1
    opts.rectRadius = ratio;
  }

  return { shapeType, opts, borderLines };
}

/**
 * Build styled text segments from node.styles for rich text rendering.
 * Returns an array of { text, options } objects for pptxgenjs addText.
 *
 * @param {string} text - The full text content
 * @param {Array} styles - Array of TextStyleSpan objects with selection and style overrides
 * @param {object} baseOptions - Base text options (color, bold, italic, fontSize, fontFace, etc.)
 * @param {object} node - The text node for additional context
 * @returns {Array} Array of text segments with individual options
 */
function buildStyledTextSegments(text, styles, baseOptions, _node) {
  if (!text || !styles || styles.length === 0) {
    return null; // No styled segments, use plain text
  }

  // Convert text to array of graphemes for proper Unicode handling
  const chars = [...text];
  const segments = [];

  // Build a map of character index -> applicable styles
  // Sort styles by selection start for consistent processing
  const sortedStyles = [...styles].sort((a, b) => {
    const aStart = a.selection?.[0] ?? 0;
    const bStart = b.selection?.[0] ?? 0;
    return aStart - bStart;
  });

  let currentIndex = 0;

  while (currentIndex < chars.length) {
    // Find styles that apply to current position
    const applicableStyle = sortedStyles.find((s) => {
      const [start, end] = s.selection || [0, 0];
      return currentIndex >= start && currentIndex < end;
    });

    if (applicableStyle) {
      const [styleStart, styleEnd] = applicableStyle.selection || [0, 0];
      // Clamp to valid range
      const segmentStart = Math.max(currentIndex, styleStart);
      const segmentEnd = Math.min(chars.length, styleEnd);

      if (segmentStart < segmentEnd) {
        const segmentText = chars.slice(segmentStart, segmentEnd).join('');
        const segmentOptions = { ...baseOptions };

        // Apply style overrides
        if (applicableStyle.fill && !isGradient(applicableStyle.fill)) {
          segmentOptions.color = parseHex(applicableStyle.fill);
        }

        if (applicableStyle.fontWeight) {
          segmentOptions.bold =
            applicableStyle.fontWeight === 'bold' || parseInt(applicableStyle.fontWeight) >= 700;
        }

        if (applicableStyle.fontStyle) {
          segmentOptions.italic = applicableStyle.fontStyle === 'italic';
        }

        segments.push({ text: segmentText, options: segmentOptions });
        currentIndex = segmentEnd;
        continue;
      }
    }

    // Find the next style start position
    let nextStyleStart = chars.length;
    for (const style of sortedStyles) {
      const [start] = style.selection || [0, 0];
      if (start > currentIndex && start < nextStyleStart) {
        nextStyleStart = start;
      }
    }

    // Add unstyled segment up to next style or end
    if (currentIndex < nextStyleStart) {
      const segmentText = chars.slice(currentIndex, nextStyleStart).join('');
      segments.push({ text: segmentText, options: { ...baseOptions } });
      currentIndex = nextStyleStart;
    }
  }

  return segments.length > 0 ? segments : null;
}

function renderText(node, slide, { x, y, w, h }) {
  const text = node.text || '';
  const textAlign = node.textAlign || 'left';

  // 使用 wrapText 判断文本布局，为文本添加冗余空间以避免 PPT 中的多余换行
  let scaledW = w;
  let isSingleLine = false;
  let textDensity = 0; // 文本密度：实际宽度 / 容器宽度

  // 单一字符文本不需要做位移调整
  const isSingleChar = [...text].length === 1;

  try {
    const wrappedResult = wrapText(node, text);
    const lineCount = wrappedResult.lines ? wrappedResult.lines.length : 0;
    const measuredWidth = wrappedResult.width || 0;
    const containerWidth = node.w || w / PX_TO_INCH;

    // 计算文本密度
    if (containerWidth > 0) {
      textDensity = measuredWidth / containerWidth;
    }

    isSingleLine = lineCount === 1;

    // 只对单行文本做宽度扩充，多行文本保持原有宽度
    // 单一字符文本不需要做宽度扩充
    if (lineCount === 1 && !isSingleChar) {
      // 基础裕量 25%
      let margin = 1.25;

      // 如果文本密度高（接近容器宽度），增加更多裕量
      if (textDensity > 0.85) {
        margin = 1.35; // 紧凑文本需要更多空间
      } else if (textDensity > 0.7) {
        margin = 1.3;
      }

      scaledW = w * margin;
    }
  } catch (err) {
    // 如果 wrapText 失败，使用默认宽度加裕量（单字符除外）
    console.warn('Failed to wrap text for PPT export:', err);
    if (!isSingleChar) {
      scaledW = w * 1.25;
    }
  }

  // 根据对齐方式调整 x 位置，以保持文本在视觉上的相对位置
  // 扩展方向应该与对齐方式相反，确保文本锚点位置不变
  const deltaW = scaledW - w;
  let adjustedX = x;

  if (deltaW > 0) {
    if (textAlign === 'center') {
      // 居中对齐：双向均匀扩展，x 向左移动一半增量，保持文本中心位置不变
      adjustedX = x - deltaW / 2;
    } else if (textAlign === 'right') {
      // 右对齐：向左扩展，x 向左移动全部增量，保持文本右边缘位置不变
      adjustedX = x - deltaW;
    }
    // 左对齐（默认）：向右扩展，x 保持不变，保持文本左边缘位置不变
  }

  // 字体大小缩放：PPT 字体渲染通常比 Web 略宽，使用 0.88 的缩放因子
  const baseFontSize = node.fontSize || 16;
  const fontSize = Math.floor(pxToPt(baseFontSize) * 0.88);

  // Calculate base text transparency
  let textTransparency = 0;
  const rgbaMatch =
    node.fill && node.fill.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch && rgbaMatch[4] !== undefined) {
    textTransparency = Math.round((1 - parseFloat(rgbaMatch[4])) * 100);
  }

  // Apply node opacity if present
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    textTransparency = Math.min(100, Math.round(100 - (100 - textTransparency) * node.opacity));
  }

  // Base text options that apply to all segments
  const baseTextOptions = {
    fontSize: fontSize,
    fontFace: node.fontFamily || 'Arial',
    color: parseHex(node.fill),
    bold: node.fontWeight === 'bold' || parseInt(node.fontWeight) >= 700,
    italic: node.fontStyle === 'italic',
    underline: node.textDecoration === 'underline',
  };

  // Apply transparency if there is any
  if (textTransparency > 0) {
    baseTextOptions.transparency = textTransparency;
  }

  // Apply character spacing if present
  if (node.letterSpacing && node.letterSpacing !== 0) {
    baseTextOptions.charSpacing = pxToPt(node.letterSpacing);
  }

  // 如果有背景色，先渲染一个 rect 作为背景层
  // 拆分为 rect + text 可以更好地控制背景渲染效果
  if (node.background) {
    const bgFill = parseFill(node.background);
    if (bgFill && bgFill.type !== 'none') {
      // 复用 rect 相关绘制能力
      const {
        shapeType,
        opts: bgOpts,
        borderLines,
      } = buildRectShapeOpts(node, { x, y, w, h }, { fillValue: node.background });
      slide.addShape(shapeType, bgOpts);
      // Draw border lines for non-uniform stroke widths
      for (const borderLine of borderLines) {
        slide.addShape('line', borderLine);
      }
    }
  }

  // Container options (position, size, alignment, etc.)
  // 文本层不再设置背景填充，由单独的 rect 负责
  const containerOpts = {
    x: adjustedX,
    y,
    w: scaledW,
    h,
    align: textAlign,
    valign:
      node.alignItems === 'center' ? 'middle' : node.alignItems === 'flex-end' ? 'bottom' : 'top',
    isTextBox: true,
    // 移除文本框默认内边距，避免 Keynote 中出现"文字嵌入"问题
    margin: 0,
    // 启用自动缩小以适应容器，防止文本溢出
    fit: 'shrink',
  };

  // 对于单行文本，禁用自动换行以保持一致性
  if (isSingleLine) {
    containerOpts.wrap = false;
  }

  // Check if we have styled text segments (multi-color support)
  const styles = node.styles;
  const styledSegments = buildStyledTextSegments(text, styles, baseTextOptions, node);

  if (styledSegments && styledSegments.length > 0) {
    // Use rich text format with multiple styled segments
    slide.addText(styledSegments, containerOpts);
  } else {
    // Use plain text with unified options
    const opts = {
      ...containerOpts,
      ...baseTextOptions,
    };
    slide.addText(text, opts);
  }
}

async function resolveImageDataUrl(store, node) {
  let src = node.src;
  if (!src) return null;

  // 已经是 dataURL，直接返回
  if (src.startsWith('data:')) return src;

  try {
    const assetProvider = store?.providers?.asset;
    if (assetProvider && typeof assetProvider.getAsset === 'function') {
      src = assetProvider.getAsset(src);
    }
  } catch {
    // ignore, fallback to original src
  }

  try {
    const blob = await fetch(src).then((res) => res.blob());
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function renderImage(store, node, slide, { x, y, w, h }) {
  let dataUrl = await resolveImageDataUrl(store, node);

  // 如果是 SVG 图片，使用 renderNodeImage 渲染为 PNG
  const isSVG = dataUrl && dataUrl.includes('data:image/svg+xml');
  if (isSVG) {
    try {
      dataUrl = await renderNodes(store, {
        nodes: [node],
        dpr: 4,
        exportMode: 'ppt',
        contentType: 'image/png',
      });
    } catch (err) {
      console.warn('Failed to render SVG as PNG:', err);
      return;
    }
  }

  if (!dataUrl) return;

  const opts = { x, y, w, h };
  // 在浏览器环境使用 data，而不是 path（path 主要用于 Node/本地文件）
  opts.data = dataUrl;

  // Apply opacity to image
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    const transparency = Math.round((1 - node.opacity) * 100);
    opts.transparency = transparency;
  }

  // 尽量将设计中的圆角语义传递给 PPT（底层支持会因 PowerPoint 版本而异）
  const numericRadius = getNumericRadius(node);
  if (numericRadius > 0 && Math.min(node.w || 0, node.h || 0) > 0) {
    const maxRadius = Math.min(node.w, node.h) / 2;
    const clamped = Math.max(0, Math.min(numericRadius, maxRadius));
    // 归一化为 0–1，用于某些 pptxgenjs 版本中的 rectRadius / rounding
    const ratio = clamped / maxRadius;
    // 这两个字段如果底层不支持会被忽略，但不会破坏现有导出行为
    // @ts-ignore - 由运行时库决定是否使用
    opts.rectRadius = ratio;
    // @ts-ignore
    opts.rounding = ratio;
  }

  slide.addImage(opts);
}

/**
 * Check if a shape type requires image fallback rendering.
 * PPT natively supports: rect, roundRect, ellipse
 * Other shapes (triangle, star, heart, etc.) need to be rendered as images.
 */
function needsImageFallback(shapeType) {
  const nativeShapes = [SHAPES.rect, SHAPES.ellipse, 'roundRect'];
  return shapeType && !nativeShapes.includes(shapeType);
}

async function renderShape(node, slide, store, { x, y, w, h }) {
  // 获取形状类型，默认为 rect
  const nodeShape = node.shape || SHAPES.rect;

  // 如果填充是渐变，或者是 PPT 不支持的复杂形状，降级为图片渲染
  const shouldRenderAsImage = isGradient(node.fill) || needsImageFallback(nodeShape);

  if (shouldRenderAsImage) {
    try {
      const dataUrl = await renderNodes(store, {
        nodes: [node],
        contentType: 'image/png',
      });
      if (dataUrl) {
        const opts = { x, y, w, h, data: dataUrl };
        // Apply opacity to image
        if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
          opts.transparency = Math.round((1 - node.opacity) * 100);
        }
        slide.addImage(opts);
        return;
      }
    } catch (err) {
      console.warn('Failed to render shape as image:', err);
      // Fall through to regular shape rendering (will use rect as fallback)
    }
  }

  // 复用 rect 相关绘制能力（fill, stroke, radius, opacity）
  const {
    shapeType: rectShapeType,
    opts,
    borderLines,
  } = buildRectShapeOpts(node, { x, y, w, h }, { fillValue: node.fill, includeDashProps: true });

  // 椭圆/圆形使用 ellipse，覆盖默认的 rect/roundRect
  const shapeType = nodeShape === SHAPES.ellipse ? 'ellipse' : rectShapeType;

  slide.addShape(shapeType, opts);

  // Draw border lines for non-uniform stroke widths
  for (const borderLine of borderLines) {
    slide.addShape('line', borderLine);
  }
}

/**
 * Render path node as image (PPT doesn't support SVG paths natively)
 */
async function renderPath(node, slide, store, { x, y, w, h }) {
  try {
    const dataUrl = await renderNodes(store, {
      nodes: [node],
      contentType: 'image/png',
    });
    if (dataUrl) {
      const opts = { x, y, w, h, data: dataUrl };
      // Apply opacity to image
      if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
        opts.transparency = Math.round((1 - node.opacity) * 100);
      }
      slide.addImage(opts);
    }
  } catch (err) {
    console.warn('Failed to render path as image:', err);
  }
}

function renderLine(node, slide, { x, y, w, h }) {
  // 解析线的点数据（相对坐标 0-1）
  const points = deserializePoints(node.points);
  if (!points || points.length < 2) return;

  let lineTransparency = 0;

  // Extract alpha from rgba fill color
  const rgbaMatch =
    node.fill && node.fill.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch && rgbaMatch[4] !== undefined) {
    lineTransparency = Math.round((1 - parseFloat(rgbaMatch[4])) * 100);
  }

  // Apply node opacity
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    lineTransparency = Math.min(100, Math.round(100 - (100 - lineTransparency) * node.opacity));
  }

  const lineOpts = {
    color: parseHex(node.fill),
    width: pxToPt(node.size || 1),
  };

  if (lineTransparency > 0) {
    lineOpts.transparency = lineTransparency;
  }

  // Handle stroke dash properties
  if (node.strokeStyle === 'dashed' && node.dashArray) {
    lineOpts.dashType = 'dash';
  }

  // 对于简单的两点线，使用起点和终点计算
  const startPoint = points[0];
  const endPoint = points[points.length - 1];

  // 计算实际的起点和终点（相对坐标 * bounding box 尺寸）
  // x, y, w, h 已经是 PPT 坐标（英寸），这里基于相对点计算偏移
  const startX = x + startPoint.x * w;
  const startY = y + startPoint.y * h;
  const endX = x + endPoint.x * w;
  const endY = y + endPoint.y * h;

  // pptxgenjs line 使用 x, y 作为起点，w, h 作为增量
  const opts = {
    x: startX,
    y: startY,
    w: endX - startX,
    h: endY - startY,
    line: lineOpts,
  };

  slide.addShape('line', opts);
}
